import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Plus, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PagesEditor = () => {
  const { studio, setStudio } = useOutletContext();
  const { getAuthHeader } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    about_text: studio?.about_text || '',
    services: studio?.services || [],
    testimonials: studio?.testimonials || []
  });
  const [newService, setNewService] = useState('');

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/studios/me`, formData, { headers: getAuthHeader() });
      setStudio(response.data);
      toast.success('Pages updated');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddService = () => {
    if (newService.trim()) {
      setFormData({
        ...formData,
        services: [...formData.services, newService.trim()]
      });
      setNewService('');
    }
  };

  const handleRemoveService = (index) => {
    setFormData({
      ...formData,
      services: formData.services.filter((_, i) => i !== index)
    });
  };

  const handleAddTestimonial = () => {
    setFormData({
      ...formData,
      testimonials: [...formData.testimonials, { name: '', text: '', rating: '5' }]
    });
  };

  const handleTestimonialChange = (index, field, value) => {
    const newTestimonials = [...formData.testimonials];
    newTestimonials[index][field] = value;
    setFormData({ ...formData, testimonials: newTestimonials });
  };

  const handleRemoveTestimonial = (index) => {
    setFormData({
      ...formData,
      testimonials: formData.testimonials.filter((_, i) => i !== index)
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="pages-editor"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Pages</h1>
          <p className="text-white/60">Edit your studio homepage content</p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-[#FFFFFF] text-black hover:bg-[#E0E0E0]"
          data-testid="save-pages-btn"
        >
          {saving ? <span className="spinner w-5 h-5"></span> : 'Save Changes'}
        </Button>
      </div>

      <div className="space-y-8">
        {/* About Section */}
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6">
          <h2 className="font-semibold mb-4">About Section</h2>
          <Label className="text-white/70 mb-2 block">About Text</Label>
          <Textarea
            value={formData.about_text}
            onChange={(e) => setFormData({ ...formData, about_text: e.target.value })}
            placeholder="Tell customers about your studio, experience, and what makes you special..."
            className="bg-[#171717] border-white/10 min-h-[150px]"
            data-testid="about-text"
          />
        </div>

        {/* Services */}
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6">
          <h2 className="font-semibold mb-4">Services</h2>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {formData.services.map((service, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 bg-[#171717] px-3 py-1.5 rounded-sm"
              >
                <span>{service}</span>
                <button
                  onClick={() => handleRemoveService(index)}
                  className="text-white/40 hover:text-red-500"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <Input
              type="text"
              value={newService}
              onChange={(e) => setNewService(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddService()}
              placeholder="Add a service (e.g., Full Vehicle Wraps)"
              className="bg-[#171717] border-white/10 flex-1"
              data-testid="new-service-input"
            />
            <Button
              onClick={handleAddService}
              variant="outline"
              className="border-white/20"
            >
              <Plus size={18} />
            </Button>
          </div>
        </div>

        {/* Testimonials */}
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Testimonials</h2>
            <Button
              onClick={handleAddTestimonial}
              variant="outline"
              className="border-white/20 text-sm"
            >
              <Plus size={16} className="mr-1" /> Add Testimonial
            </Button>
          </div>

          <div className="space-y-4">
            {formData.testimonials.map((testimonial, index) => (
              <div key={index} className="bg-[#171717] border border-white/5 rounded-sm p-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70 mb-2 block text-sm">Name</Label>
                      <Input
                        type="text"
                        value={testimonial.name}
                        onChange={(e) => handleTestimonialChange(index, 'name', e.target.value)}
                        placeholder="John D."
                        className="bg-[#0F0F0F] border-white/10"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70 mb-2 block text-sm">Rating</Label>
                      <select
                        value={testimonial.rating}
                        onChange={(e) => handleTestimonialChange(index, 'rating', e.target.value)}
                        className="w-full bg-[#0F0F0F] border border-white/10 rounded-sm h-10 px-3"
                      >
                        <option value="5">5 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="3">3 Stars</option>
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveTestimonial(index)}
                    className="ml-4 text-white/40 hover:text-red-500"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div>
                  <Label className="text-white/70 mb-2 block text-sm">Review Text</Label>
                  <Textarea
                    value={testimonial.text}
                    onChange={(e) => handleTestimonialChange(index, 'text', e.target.value)}
                    placeholder="What did they say about your work?"
                    className="bg-[#0F0F0F] border-white/10"
                  />
                </div>
              </div>
            ))}

            {formData.testimonials.length === 0 && (
              <p className="text-white/40 text-sm text-center py-8">
                No testimonials yet. Add some to build trust with visitors.
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PagesEditor;
